import React from "react";

interface DeleteConfirmationModalProps {
  isOpen: boolean;
  campaignName: string;
  isDraft: boolean;
  onConfirm: () => void;
  onCancel: () => void;
  isDeleting?: boolean;
}

const DeleteConfirmationModal: React.FC<DeleteConfirmationModalProps> = ({
  isOpen,
  campaignName,
  isDraft,
  onConfirm,
  onCancel,
  isDeleting = false,
}) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 backdrop-blur-sm">
      {/* Gradient Border Wrapper */}
      <div
        className="relative w-full mx-4"
        style={{
          maxWidth: "600px",
          borderRadius: "40px",
          padding: "1px",
          background:
            "linear-gradient(180deg, rgba(130, 135, 152, 0) 0%, rgba(130, 135, 152, 0.4) 100%)",
        }}
      >
        {/* Actual Modal */}
        <div
          className="relative bg-[#0e0e10] shadow-2xl overflow-hidden"
          style={{
            borderRadius: "40px",
            paddingTop: "64px",
            paddingBottom: "64px",
          }}
        >
          {/* 🔥 Centered Top Gradient */}
          <div
            className="absolute left-1/2 -translate-x-1/2 opacity-80 blur-3xl"
            style={{
              top: "-70px",
              width: "460px",
              height: "140px",
              background:
                "linear-gradient(226.45deg, #EF4444 12.8%, #F87171 93.65%)",
            }}
          />

          {/* Content */}
          <div className="relative z-10 px-[68px]">
            {/* Warning Icon */}
            <div className="flex justify-center mb-6">
              <div className="w-20 h-20 rounded-full bg-red-500/20 flex items-center justify-center">
                <svg width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M20 40C31.0457 40 40 31.0457 40 20C40 8.9543 31.0457 0 20 0C8.9543 0 0 8.9543 0 20C0 31.0457 8.9543 40 20 40Z" fill="#EF4444" fillOpacity="0.2"/>
                  <path d="M20 12V20M20 28V20" stroke="#EF4444" strokeWidth="3" strokeLinecap="round"/>
                </svg>
              </div>
            </div>

            {/* Title */}
            <h2 className="text-white text-center text-[32px] font-semibold leading-[130%]">
              Delete {isDraft ? "Draft" : "Campaign"}?
            </h2>

            {/* Message */}
            <p className="text-white/70 text-center mt-4 text-[16px] leading-[150%]">
              Are you sure you want to delete <span className="text-white font-semibold">{campaignName}</span>?
              <br />
              {isDraft ? (
                <>
                  This will permanently delete your draft campaign.
                  <br />
                  You won't be able to recover it later.
                </>
              ) : (
                <>
                  This will permanently delete your campaign from Meta.
                  <br />
                  This action cannot be undone.
                </>
              )}
            </p>

            {/* Buttons */}
            <div className="mt-8 flex justify-center gap-4">
              {/* Cancel Button */}
              <button
                onClick={onCancel}
                disabled={isDeleting}
                className="w-[180px] px-12 py-3 rounded-full font-semibold text-white transition-transform hover:scale-105 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed border border-white/20 hover:border-white/40"
              >
                Cancel
              </button>

              {/* Delete Button */}
              <div
                className="inline-flex rounded-full p-[1px] overflow-hidden"
                style={{
                  background:
                    "linear-gradient(90deg, rgba(239, 68, 68, 0.2) 0%, #EF4444 100%)",
                }}
              >
                <button
                  onClick={onConfirm}
                  disabled={isDeleting}
                  className="w-[180px] px-12 py-3 rounded-full font-semibold text-white transition-transform hover:scale-105 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  style={{
                    background: "linear-gradient(to right, #7F1D1D, #EF4444)",
                  }}
                >
                  {isDeleting ? (
                    <>
                      <svg className="animate-spin" width="16" height="16" viewBox="0 0 24 24" fill="none">
                        <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" className="opacity-25" />
                        <path fill="currentColor" className="opacity-75" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                      </svg>
                      Deleting...
                    </>
                  ) : (
                    "Delete"
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DeleteConfirmationModal;
